import { Component, computed, inject } from '@angular/core';
import { LvrRedirectNav } from '../../../core/classes/redirect-nav.class';
import { LvrHeading } from '../../../core/components/heading/heading';
import { ContactPointType } from '../../../api/app-types/contact-point.app';
import { CarouselElement, NavCarousel } from '../../../core/components/nav-carousel/nav-carousel';
import { Institution, InstitutionPerson } from '../../../api/app-types/institution.app';
import { TranslocoPipe } from '@jsverse/transloco';
import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { InstitutionPersonDeactivateDialog } from '../institution-person-deactivate-dialog/institution-person-deactivate-dialog';
import { DatePipe } from '@angular/common';
import { CLARIFICATION_TYPE } from '../../../api/app-types/relative.app';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { NeighborContext } from '../../../api/app-types/neighbors.app';

@Component({
	selector: 'lvr-institution-person-details',
	imports: [LvrHeading, NavCarousel, TranslocoPipe, DatePipe],
	templateUrl: './institution-person-details.html',
	styleUrl: './institution-person-details.scss',
})
export class InstitutionPersonDetails extends LvrRedirectNav {
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);

	private readonly personNeighbors = this.resolveDataAsSignal<NeighborContext<InstitutionPerson>>('personNeighbors');
	private readonly institution = this.resolveDataAsSignal<Institution>('institution');
	private readonly institutionId = this.institution()?.id;
	public readonly person = this.resolveDataAsSignal<InstitutionPerson>('person');

	public readonly personName = computed(() =>
		this.person()?.firstName
			? `${this.person()?.name}, ${this.person()?.firstName}`.trim()
			: `${this.person()?.name ?? ''}`.trim(),
	);
	public readonly backLink = computed(() => `/desk/institution/${this.institutionId}/persons`);
	public readonly disableDeleteTranslationKey = 'institution.persons.details.deactivate_disable';
	public readonly canEditInstitutionPersons = hasPermission(this.session.permissions, 'InstitutionPersons.Edit');
	public readonly canDeleteInstitutionPersons = hasPermission(this.session.permissions, 'InstitutionPersons.Delete');

	public readonly preferredCommunicationPoints = computed(
		() => this.person()?.contactPoints.filter((cp) => cp.preferredWayOfContact) ?? [],
	);

	public readonly otherCommunicationPoints = computed(
		() => this.person()?.contactPoints.filter((cp) => !cp.preferredWayOfContact) ?? [],
	);

	public readonly contactTypes = {
		phone: ContactPointType.PHONE,
		email: ContactPointType.EMAIL,
	};

	public readonly clarificationTypes = [
		{ value: CLARIFICATION_TYPE.PHONE, label: 'telefonisch' },
		{ value: CLARIFICATION_TYPE.IN_PERSON, label: 'persönlich' },
		{ value: CLARIFICATION_TYPE.MAIL, label: 'schriftlich / email' },
	];

	public getClarificationLabel(id?: CLARIFICATION_TYPE) {
		const type = this.clarificationTypes.find((t) => t.value === id);
		return type ? type.label : '';
	}

	public readonly carouselElements = computed(() => {
		const neighbors = this.personNeighbors();

		if (!neighbors) return [];

		return [neighbors.previous, neighbors.current, neighbors.next]
			.filter((person): person is InstitutionPerson => !!person && !person.isDeactivated)
			.map((person) => ({
				id: person.id.toString(),
				label: person.firstName ? `${person.name}, ${person.firstName}`.trim() : `${person.name ?? ''}`.trim(),
			}));
	});

	public readonly currentIndex = computed(() => {
		const person = this.person();
		const elements = this.carouselElements();

		if (!person || elements.length === 0) return 0;

		const index = elements.findIndex((e) => e.id === person.id.toString());

		return index >= 0 ? index : 0;
	});

	/**
	 * Handles the navigation from the nav carousel.
	 * Updates the route based on the selected element.
	 */
	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.navigateAfterFinished(['../', event.element.id], { relativeTo: this.route });
	}

	/**
	 * Navigates to 'edit' view.
	 */
	public openEdit() {
		if (!this.canEditInstitutionPersons()) return;
		this.navigateAfterFinished(['edit'], { relativeTo: this.route });
	}

	/**
	 * Open deactivate dialog, reroutes after deletion.
	 */
	public async openDialog() {
		if (!this.canDeleteInstitutionPersons()) return;

		await lastValueFrom(
			this.dialog
				.open(InstitutionPersonDeactivateDialog, {
					width: '600px',
					data: {
						person: this.person(),
						institution: this.institution(),
						onDeactivated: async () => {
							await this.navigateAfterFinished(['../'], { relativeTo: this.route });
						},
					},
				})
				.afterClosed(),
		);
	}
}
